import Button from '@atlaskit/button';
import { token } from '@atlaskit/tokens';
import {
  TextField,
  TextFieldLabel,
  TextFieldMessage,
} from '@atlassian/learning-components';
import { css } from '@compiled/react';
import { Editor } from '@tiptap/react';
import { useEffect, useState } from 'react';
import { isValidUrl } from 'utils';
import { UnstyledButton } from '../UnstyledButton';

export const AddLinkPopup = ({
  editor,
  onSubmitForm,
  onCancel,
  selectedText,
  selectedLink,
}: {
  editor: Editor;
  onSubmitForm: (editor: Editor, link?: string, displayText?: string) => void;
  onCancel: (e: React.MouseEvent<HTMLElement>) => void;
  selectedText?: string;
  selectedLink?: string;
}) => {
  const [link, setLink] = useState(selectedLink ?? '');
  const [text, setText] = useState(selectedText ?? '');
  const [validLink, setValidLink] = useState(true);

  // Sometimes, the editor hasn't updated when this component is created
  // So we need to update the link and text if the props change
  useEffect(() => {
    setLink(selectedLink ?? '');
    setText(selectedText ?? '');
    return () => {
      setLink('');
      setText('');
      setValidLink(true);
    };
  }, [selectedLink, selectedText]);

  const onChangeLink = (e: React.FormEvent<HTMLInputElement>) => {
    setValidLink(true);
    setLink(e.currentTarget.value);
  };
  const onSubmit = () => {
    if (link && !isValidUrl(link)) {
      setValidLink(false);
      // Not sure if this is necessary but if there are errors,
      // this will show the red border around the input element instead of remaining foucsed
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
      return;
    }
    onSubmitForm(editor, link, text);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Enter') {
      onSubmitForm(editor, link, text);
    }
  };

  return (
    <div css={popupStyles} onKeyDown={onKeyDown}>
      <div css={flexColumn}>
        <div>
          <TextFieldLabel isRequired={false} labelText="Add a link" />
          <TextField
            value={link}
            onChange={onChangeLink}
            placeholder="Add a new link"
            isInvalid={!validLink}
          />
          {!validLink && (
            <TextFieldMessage
              type="error"
              message="Please enter a valid link"
            />
          )}
        </div>
        <div>
          <TextFieldLabel isRequired={false} labelText="Display text" />
          <TextField
            value={text}
            onChange={(e) => {
              setText(e.currentTarget.value);
            }}
            placeholder="Display text"
          />
        </div>
        <div css={addLinkButtonsStyles}>
          <UnstyledButton onClick={onCancel}>
            <div css={cancelContainerStyles}>
              <p css={cancelTextStyles}>{'Cancel'}</p>
            </div>
          </UnstyledButton>
          <Button onClick={onSubmit} appearance="primary">
            {'Save'}
          </Button>
        </div>
      </div>
    </div>
  );
};

const popupStyles = css({
  width: '320px',
  padding: '16px',
  borderRadius: token('space.100'),
  boxShadow: token('elevation.surface'),
  backgroundColor: 'white',
  border: `1px solid ${token('color.border')}`,
});

const flexColumn = css({
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
});

const addLinkButtonsStyles = css({
  display: 'flex',
  justifyContent: 'flex-end',
  fontSize: '16px',
  gap: '8px',
});

const cancelContainerStyles = css({
  padding: `${token('space.100')} ${token('space.150')}`,
});

const cancelTextStyles = css({
  color: token('color.text.brand'),
  fontSize: '16px',
});
